<template>
  <div :class="$style.container">
    <div :class="$style.headline">
      <div :class="$style['bike-details']">
        <h3 v-if="bike.name" :class="$style['bike-name']">{{ variantName }}</h3>
        <h1>{{ bikeName }}</h1>
        <div :class="$style['bike-info']">
          <span v-if="bikeSKU">{{ $t('complaintForm.ticket.battery.sku') }}: {{ bikeSKU }}</span>
          <span v-if="bikeSerialNo"
            >{{ $t('complaintForm.ticket.battery.serial_number') }}: {{ bikeSerialNo }}</span
          >
        </div>
      </div>
      <div :class="$style['bike-image']">
        <img :src="firstImage" :alt="bikeName" />
      </div>
    </div>
    <div v-if="bikeData" :class="$style['tree-view']">
      <div :class="$style.row">
        <div v-if="mappedItems" :class="$style.tree">
          <TreeView :items="mappedItems" @currentItem="setCurrentItem" />
        </div>
        <v-divider vertical class="desktop-only"></v-divider>
        <div :class="$style.selection">
          <ComplaintFormComponentErrorDesc :selected-items="selectedItems" />
        </div>
      </div>
      <ComplaintFormStepperControls
        :is-valid="selectedItems.length > 0"
        :prev-step="prevStep"
        :next-step="submit"
      />
    </div>
    <FeatureSwitch name="fe-componenttable">
      <div v-if="mappedItems">
        <table style="width=100%;">
          <thead>
            <tr>
              <th style="padding: 0.5rem 1rem">cat</th>
              <th style="padding: 0.5rem 1rem">name</th>
              <th style="padding: 0.5rem 1rem">item_no</th>
              <th style="padding: 0.5rem 1rem">hierarchy</th>
              <th style="padding: 0.5rem 1rem">component</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(cat, index) in mappedItems">
              <template v-for="child in cat.children">
                <tr :key="`${index}${child.id}`">
                  <td :key="`${index}${child.id}-cat`" style="padding: 0.125rem 1rem">
                    {{ cat.title }}
                  </td>
                  <td :key="`${index}${child.id}-name`" style="padding: 0.125rem 1rem">
                    {{ child.name }}
                  </td>
                  <td :key="`${index}${child.id}-itemNo`" style="padding: 0.125rem 1rem">
                    {{ child.itemNo }}
                  </td>
                  <td :key="`${index}${child.id}-hierarchy`" style="padding: 0.125rem 1rem">
                    {{ child.hierarchy }}
                  </td>
                  <td :key="`${index}${child.id}-component`" style="padding: 0.125rem 1rem">
                    {{ child.component }}
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </FeatureSwitch>
  </div>
</template>

<script>
import { get, set } from 'lodash-es'
import { mapMutations, mapGetters } from 'vuex'
import { isUndefined, snakeCase, sortBy } from 'lodash-es'
import { CONSTANTS } from '@/js/helper/dcw-product-list-constants.js'
import TreeView from '@/js/components/Forms/TreeView/TreeView.vue'
import StepperMixin from '@/js/mixins/stepper-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'
import StoreMetaMixin from '@/js/mixins/store-meta-mixin.js'
import ComplaintFormStepperControls from '@/js/components/Forms/ComplaintForm/ComplaintFormStepperControls.vue'
import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'
import { getPreviewImage } from '@/js/helper/aimeos-image-helper.js'
import API_CONSTANTS from '@/js/helper/api-constants'
import IMAGE_CONSTANTS from '@/js/helper/image-constants.js'

import ComplaintFormComponentErrorDesc from './ComplaintFormComponentErrorDesc.vue'
import FeatureSwitch from '@/js/components/Shared/FeatureSwitch.js'

export default {
  components: {
    TreeView,
    ComplaintFormComponentErrorDesc,
    ComplaintFormStepperControls,
    FeatureSwitch,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin, StoreMetaMixin],
  props: {
    ...StepperMixin.props,
  },
  validations: {},
  data() {
    return {
      apiData: {},
      fallbackImage:
        '/typo3conf/ext/dealerportal_sitepackage/Resources/Public/wp/images/placeholder.png',
      claim: [], // form object from the tree view
      currentItem: {}, // last clicked checkbox
    }
  },
  computed: {
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      getBikeData: FORM_GETTER.BIKE_DATA,
      selectedItems: FORM_GETTER.CHECKBOX_SELECTIONS,
    }),
    bikeData() {
      return this.getBikeData[CONSTANTS.HBS_CUSTSERVEITEM_VIEW]
    },
    bike() {
      return this.bikeData[CONSTANTS.HBS_BIKE]
    },
    bikeName() {
      return this.bike.ItemDesc
    },
    variantName() {
      return this.bike.name && this.bike.name.length ? this.bike.name[0] : this.bikeName
    },
    bikeSKU() {
      return this.bike.Item
    },
    bikeSerialNo() {
      return this.bike.SerialNo
    },
    bikeImages() {
      return this.bike?.media
    },
    firstImage() {
      return this.bikeImages
        ? `${this.contentBaseurl}/${
            getPreviewImage({
              image: set({}, API_CONSTANTS.AIMEOS.MEDIA.PATHS.PREVIEWS, this.bikeImages),
              imageMinSize: IMAGE_CONSTANTS.IMAGE_MIN_SIZE.PRODUCT_LIST,
            })?.url
          }`
        : undefined
    },
    mappedItems() {
      return this.getBikeDataItems(this.bikeData[CONSTANTS.HBS_HEADERS])
    },
    getFormSelectionByName() {
      return this.claim.filter(selection => selection.name === this.currentItem.item.name).pop()
    },
  },
  watch: {
    // if the user inserts a new serial number = remove current selections
    getBikeData: {
      handler() {
        this.removeAllCheckboxSelections()
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
      setPending: FORM_MUTATION.SET_FORM_PENDING,
      setBikeSelections: FORM_MUTATION.SET_COMPONENT_SELECTIONS,
      removeAllCheckboxSelections: FORM_MUTATION.REMOVE_ALL_CHECKBOX_SELECTIONS,
    }),
    /*
      items = SpareParts API response header object
      creates the full converted object for the treeView
    */
    getBikeDataItems(items) {
      return sortBy(
        items
          .map(item => {
            const name = item[CONSTANTS.HBS_HEADER_NO]
            const description = get(item, CONSTANTS.HBS_DESCRIPTION)
            return {
              id: item[CONSTANTS.HBS_HEADER_NO],
              name,
              title: description
                ? description
                : this.$t(`complaintForm.spare_parts_api_components.${name.toLowerCase()}`),
              type: 'header',
              children: this.getRecursive(item),
            }
          })
          .filter(item => !isUndefined(item.name) && item.name !== 'VERPACKUNG'),
        ['title'],
      )
    },
    /*
      item = Headeritem from response
      checks if the item has groups, parts or both and returns the converted objects
    */
    getRecursive(item) {
      if (this.hasGroups(item) && this.hasParts(item)) {
        return [...this.getGroups(item), ...this.getParts(item)]
      } else if (this.hasGroups(item)) {
        return this.getGroups(item)
      } else if (this.hasParts(item)) {
        return this.getParts(item)
      }
    },
    /*
      item = item with groups
      converts and creates the group children recursively
    */
    getGroups(item) {
      return item[CONSTANTS.HBS_PRODUCT_GROUPS].map((groupItem, index) => {
        return {
          id: snakeCase(`${groupItem[CONSTANTS.HBS_PRODUCT_GROUP_DESC]}_${index}`),
          component: groupItem[CONSTANTS.HBS_COMPONENT],
          name: groupItem[CONSTANTS.HBS_PRODUCT_GROUP_DESC],
          children: this.getRecursive(groupItem),
        }
      })
    },
    getParts(partItems) {
      return partItems[CONSTANTS.HBS_PRODUCT_PARTS].map((partItem, index) => {
        return {
          id: snakeCase(
            `${partItem[CONSTANTS.HBS_PRODUCT_DESC]}_${
              partItem[CONSTANTS.HBS_PRODUCT_Nr]
            }_${index}`,
          ),
          component: partItem[CONSTANTS.HBS_COMPONENT],
          name: partItem[CONSTANTS.HBS_PRODUCT_DESC],
          quantity: partItem[CONSTANTS.HBS_PRODUCT_QUANTITY],
          itemNo: partItem[CONSTANTS.HBS_PRODUCT_NO],
          hierarchy: get(partItem, CONSTANTS.HBS_PRODUCT_HIERARCHY),
        }
      })
    },
    hasParts(item) {
      return !isUndefined(item[CONSTANTS.HBS_PRODUCT_PARTS])
    },
    hasGroups(item) {
      return !isUndefined(item[CONSTANTS.HBS_PRODUCT_GROUPS])
    },
    hasFormData(formData) {
      return this.claim.filter(selection => selection.part === formData.part).length > 0
    },
    setCurrentItem(event) {
      this.currentItem = event
    },
    setFormData(event) {
      if (this.hasFormData(event)) {
        this.claim.forEach((selection, index) => {
          if (selection.part === event.part) {
            this.claim[index] = event
          }
        })
      } else {
        this.claim.push(event)
      }
    },
    submitCallback() {
      this.setBikeSelections(this.selectedItems)
      this.updateFormData({ claim: this.claim })
      this.nextStep()
    },
  },
}
</script>

<style lang="scss" module>
.bike-name {
  margin-bottom: 0;

  color: var(--grey-lighten-1);
}

.headline {
  @media screen and (max-width: map-get($grid-breakpoints, 'md')) {
    grid-gap: 0.5rem;
    grid-template-columns: 1fr;

    h1 {
      font-size: var(--font-3xl);
    }

    .bike-image {
      margin-left: 0 !important;
    }
  }
  display: grid;
  grid-gap: 0;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 1rem;

  border: 1px solid var(--primary);

  .bike-details {
    display: flex;
    flex-direction: column;

    .bike-info {
      display: flex;
      align-items: center;
      justify-content: space-between;

      span {
        color: var(--grey);
      }
    }

    h1 {
      margin-bottom: 0;
    }
  }

  .bike-image {
    display: flex;
    align-items: center;
    margin-left: auto;

    img {
      max-height: 12rem;
    }
  }
}

.row {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    grid-template-columns: 10fr 1fr 10fr;
  }
  display: grid;
  grid-template-columns: 1fr;
}
</style>
